import { config, getAssetUrl } from '../config/config';

const API_BASE = config.API_BASE_URL;

class CreatorContentService {
  // ==================== GALLERY MANAGEMENT ====================

  // Get creator's gallery items
  async getGalleryItems(creatorId) {
    try {
      console.log('=== GET GALLERY ITEMS DEBUG ===');
      console.log('Fetching gallery items for creator ID:', creatorId);
      console.log('API URL:', `${API_BASE}/creator-content/gallery/${creatorId}`);
      
      const response = await fetch(`${API_BASE}/creator-content/gallery/${creatorId}`);
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) throw new Error('Failed to fetch gallery items');
      
      const data = await response.json();
      console.log('Response data:', data);
      console.log('Gallery items count:', data.galleryItems ? data.galleryItems.length : 'undefined');
      
      // Transform the data to add the image field and normalize field names
      const transformedItems = data.galleryItems.map(item => ({
        ...item,
        image: this.getImageUrl(item.image_url),
        allowDownload: item.allow_download
      }));
      
      console.log('Transformed items:', transformedItems);
      
      return transformedItems;
    } catch (error) {
      console.error('Error fetching gallery items:', error);
      throw error;
    }
  }

  // Add new gallery item
  async addGalleryItem(formData) {
    try {
      const token = localStorage.getItem('token');
      console.log('=== GALLERY SERVICE DEBUG ===');
      console.log('Token:', token ? 'Present' : 'Missing');
      console.log('API Base:', `${API_BASE}/creator-content/gallery`);
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
      }
      
      // Check if image is present in FormData
      const hasImage = formData.has('image') && formData.get('image') instanceof File;
      if (!hasImage) {
        throw new Error('Image file is required for new gallery items');
      }
      
      const response = await fetch(`${API_BASE}/creator-content/gallery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error text:', errorText);
        throw new Error(`Failed to add gallery item: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Response data:', data);
      return data.galleryItem;
    } catch (error) {
      console.error('Error adding gallery item:', error);
      throw error;
    }
  }

  // Update gallery item
  async updateGalleryItem(id, formData) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/creator-content/gallery/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to update gallery item');
      const data = await response.json();
      return data.galleryItem;
    } catch (error) {
      console.error('Error updating gallery item:', error);
      throw error;
    }
  }

  // Delete gallery item
  async deleteGalleryItem(id) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/creator-content/gallery/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete gallery item');
      return true;
    } catch (error) {
      console.error('Error deleting gallery item:', error);
      throw error;
    }
  }

  // ==================== FEED POSTS MANAGEMENT ====================

  // Get creator's feed posts
  async getFeedPosts(creatorId) {
    try {
      const response = await fetch(`${API_BASE}/creator-content/posts/${creatorId}`);
      if (!response.ok) throw new Error('Failed to fetch feed posts');
      const data = await response.json();
      return data.posts;
    } catch (error) {
      console.error('Error fetching feed posts:', error);
      throw error;
    }
  }

  // Add new feed post
  async addFeedPost(postData) {
    try {
      const token = localStorage.getItem('token');
      
      // Create FormData using the post-specific method
      const formData = this.createPostFormData(postData, postData.imageFile);
      
      // Check if image is present in FormData (optional for now)
      const hasImage = formData.has('image') && formData.get('image') instanceof File;
      console.log('Image present in FormData:', hasImage);
      
      const response = await fetch(`${API_BASE}/creator-content/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend error response:', errorData);
        throw new Error(errorData.error || `Failed to add feed post (${response.status})`);
      }
      
      const data = await response.json();
      return data.post;
    } catch (error) {
      console.error('Error adding feed post:', error);
      throw error;
    }
  }

  // Update feed post
  async updateFeedPost(id, formData) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/creator-content/posts/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to update feed post');
      const data = await response.json();
      return data.post;
    } catch (error) {
      console.error('Error updating feed post:', error);
      throw error;
    }
  }

  // Delete feed post
  async deleteFeedPost(id) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/creator-content/posts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete feed post');
      return true;
    } catch (error) {
      console.error('Error deleting feed post:', error);
      throw error;
    }
  }

  // ==================== SHOP MANAGEMENT ====================

  // Get creator's shop items (public - active only)
  async getShopItems(creatorId) {
    try {
      const response = await fetch(`${API_BASE}/creator-content/shop/${creatorId}`);
      if (!response.ok) throw new Error('Failed to fetch shop items');
      const data = await response.json();
      return data.shopItems;
    } catch (error) {
      console.error('Error fetching shop items:', error);
      throw error;
    }
  }

  // Get creator's shop items for management (all items including inactive)
  async getShopItemsForManagement() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/creator-content/shop/management/items`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch shop items for management');
      const data = await response.json();
      return data.shopItems;
    } catch (error) {
      console.error('Error fetching shop items for management:', error);
      throw error;
    }
  }

  // Add new shop item
  async addShopItem(formData) {
    try {
      const token = localStorage.getItem('token');
      
      // Check if image is present in FormData
      const hasImage = formData.has('image') && formData.get('image') instanceof File;
      if (!hasImage) {
        throw new Error('Image file is required for shop items');
      }
      
      const response = await fetch(`${API_BASE}/creator-content/shop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to add shop item');
      const data = await response.json();
      return data.shopItem;
    } catch (error) {
      console.error('Error adding shop item:', error);
      throw error;
    }
  }

  // Update shop item
  async updateShopItem(id, formData) {
    try {
      const token = localStorage.getItem('token');
      
      // Debug: Log what's being sent
      console.log('=== FRONTEND DEBUG ===');
      console.log('Updating shop item with ID:', id);
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
      
      const response = await fetch(`${API_BASE}/creator-content/shop/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend error response:', errorData);
        throw new Error('Failed to update shop item');
      }
      const data = await response.json();
      return data.shopItem;
    } catch (error) {
      console.error('Error updating shop item:', error);
      throw error;
    }
  }

  // Delete shop item
  async deleteShopItem(id) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/creator-content/shop/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete shop item');
      return true;
    } catch (error) {
      console.error('Error deleting shop item:', error);
      throw error;
    }
  }

  // Toggle shop item status (active/inactive)
  async toggleShopItemStatus(id) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/creator-content/shop/${id}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to toggle shop item status');
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error toggling shop item status:', error);
      throw error;
    }
  }

  // ==================== PROFILE SETTINGS ====================

  // Get creator's profile settings
  async getCreatorProfile(creatorId) {
    try {
      const response = await fetch(`${API_BASE}/creator-content/profile/${creatorId}`);
      if (!response.ok) throw new Error('Failed to fetch creator profile');
      const data = await response.json();
      return data.profile;
    } catch (error) {
      console.error('Error fetching creator profile:', error);
      throw error;
    }
  }

  // Update creator's profile settings
  async updateCreatorProfile(profileData) {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/creator-content/profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });
      
      if (!response.ok) throw new Error('Failed to update creator profile');
      const data = await response.json();
      return data.profile;
    } catch (error) {
      console.error('Error updating creator profile:', error);
      throw error;
    }
  }

  // ==================== UTILITY FUNCTIONS ====================

  // Create FormData for gallery items
  createFormData(data, imageFile = null) {
    const formData = new FormData();
    
    console.log('=== CREATE FORMDATA DEBUG (GALLERY) ===');
    console.log('Input data:', data);
    console.log('Image file:', imageFile);
    console.log('Image file type:', imageFile ? typeof imageFile : 'null');
    console.log('Image file instanceof File:', imageFile instanceof File);
    
    // Only add the fields the backend expects for gallery items
    const expectedFields = ['title', 'description', 'visibility', 'allowDownload'];
    
    expectedFields.forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
        console.log(`Added field: ${key} =`, data[key]);
      }
    });
    
    // Add image file if provided
    if (imageFile && imageFile instanceof File) {
      formData.append('image', imageFile);
      console.log('Added image file:', imageFile.name, imageFile.size, 'bytes');
    } else {
      console.log('No image file provided or invalid file object');
    }
    
    // Debug FormData contents
    console.log('Final FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
    }
    
    return formData;
  }

  // Create FormData for feed posts
  createPostFormData(data, imageFile = null) {
    const formData = new FormData();
    
    console.log('=== CREATE FORMDATA DEBUG (POST) ===');
    console.log('Input data:', data);
    console.log('Image file:', imageFile);
    console.log('Image file type:', imageFile ? typeof imageFile : 'null');
    console.log('Image file instanceof File:', imageFile instanceof File);
    
    // Add the fields the backend expects for feed posts
    const expectedFields = ['title', 'content', 'visibility'];
    
    expectedFields.forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
        console.log(`Added field: ${key} =`, data[key]);
      }
    });
    
    // Add image file if provided
    if (imageFile && imageFile instanceof File) {
      formData.append('image', imageFile);
      console.log('Added image file:', imageFile.name, imageFile.size, 'bytes');
    } else {
      console.log('No image file provided or invalid file object');
    }
    
    // Debug FormData contents
    console.log('Final FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value);
    }
    
    return formData;
  }

  // Get full image URL
  getImageUrl(imagePath) {
    return getAssetUrl(imagePath);
  }
}

export default new CreatorContentService();
